<template>
  <div class="landing-container">
    <!-- Redirect to Feed if logged in -->
    <div v-if="$store.state.isLoggedIn" class="redirect-container">
      <div class="redirect-message">
        <font-awesome-icon icon="sync-alt" spin class="redirect-icon" />
        <p>{{ $t('home.redirectingToFeed') || 'Redirecting to your activity feed...' }}</p>
      </div>
    </div>

    <!-- Original Landing Page for Non-logged-in Users -->
    <div v-else>

    <!-- Login CTA Section -->
    <section class="login-cta-section">
      <div class="container">
        <div class="login-cta-content">
          <div class="login-cta-visual">
            <div class="login-icon-wrapper">
              <font-awesome-icon icon="user-circle" class="login-main-icon" />
              <div class="icon-pulse"></div>
            </div>
          </div>
          <div class="login-cta-text">
            <h2 class="login-cta-title">{{ $t('home.joinCommunity') || 'Join Our Running Community!' }}</h2>
            <p class="login-cta-subtitle">{{ $t('home.loginBenefit') || 'Connect with fellow runners, track your progress, and access exclusive features' }}</p>
            <div class="login-benefits">
              <div class="benefit-item">
                <font-awesome-icon icon="users" class="benefit-icon" />
                <span>{{ $t('home.connectRunners') || 'Connect with runners' }}</span>
              </div>
              <div class="benefit-item">
                <font-awesome-icon icon="chart-line" class="benefit-icon" />
                <span>{{ $t('home.trackProgress') || 'Track your progress' }}</span>
              </div>
              <div class="benefit-item">
                <font-awesome-icon icon="trophy" class="benefit-icon" />
                <span>{{ $t('home.exclusiveFeatures') || 'Exclusive features' }}</span>
              </div>
            </div>
          </div>
          <div class="login-cta-actions">
            <router-link :to="{ name: 'login' }" class="login-primary-btn">
              <font-awesome-icon icon="sign-in-alt" class="btn-icon" />
              <span>{{ $t('home.loginNow') || 'Login with Strava' }}</span>
              <div class="btn-shine"></div>
            </router-link>
            <p class="login-help-text">{{ $t('home.freeToJoin') || 'It\'s free and takes just seconds!' }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Special Features Section -->
    <section class="special-features-section">
      <div class="container">
        <h2 class="special-features-title">{{ $t('home.specialFeatures') || 'Special Features' }}</h2>
        <p class="special-features-subtitle">{{ $t('home.specialFeaturesDesc') || 'Discover our unique tools designed for runners' }}</p>

        <!-- Subscription Pricing Component -->
        <SubscriptionPricing />

        <div class="features-grid">
          <!-- MakeKorlor Feature Card -->
          <div class="feature-card special-card makekorlor-card">
            <div class="card-visual">
              <div class="card-icon">
                <font-awesome-icon icon="map-marked-alt" />
              </div>
              <div class="card-badge">NEW</div>
            </div>
            <div class="card-content">
              <h3 class="card-title">{{ $t('home.makeKorlorTitle') || 'MakeKorlor' }}</h3>
              <p class="card-description">{{ $t('home.makeKorlorDesc') || 'Create stunning animated route videos from your GPX files with music synchronization' }}</p>
              <div class="card-features">
                <div class="feature-item">
                  <font-awesome-icon icon="upload" />
                  <span>{{ $t('home.uploadGpx') || 'Upload GPX files' }}</span>
                </div>
                <div class="feature-item">
                  <font-awesome-icon icon="music" />
                  <span>{{ $t('home.addMusic') || 'Add background music' }}</span>
                </div>
                <div class="feature-item">
                  <font-awesome-icon icon="play-circle" />
                  <span>{{ $t('home.animateRoute') || 'Animate your route' }}</span>
                </div>
                <div class="feature-item">
                  <font-awesome-icon icon="download" />
                  <span>{{ $t('home.exportVideo') || 'Export animated videos' }}</span>
                </div>
              </div>
              <router-link :to="{ name: 'makeKorlor' }" class="card-cta">
                <font-awesome-icon icon="rocket" />
                {{ $t('home.tryMakeKorlor') || 'Try MakeKorlor' }}
              </router-link>
            </div>
          </div>

          <!-- Training Plan Feature Card -->
          <div class="feature-card special-card training-plan-card">
            <div class="card-visual">
              <div class="card-icon">
                <font-awesome-icon icon="dumbbell" />
              </div>
              <div class="card-badge">PRO</div>
            </div>
            <div class="card-content">
              <h3 class="card-title">{{ $t('home.trainingPlanTitle') || 'Training Plans' }}</h3>
              <p class="card-description">{{ $t('home.trainingPlanDesc') || 'Personalized training plans for 5K, 10K, half marathon, and marathon distances' }}</p>
              <div class="card-features">
                <div class="feature-item">
                  <font-awesome-icon icon="calendar-alt" />
                  <span>{{ $t('home.weeklySchedules') || 'Weekly schedules' }}</span>
                </div>
                <div class="feature-item">
                  <font-awesome-icon icon="bullseye" />
                  <span>{{ $t('home.goalSetting') || 'Goal setting' }}</span>
                </div>
                <div class="feature-item">
                  <font-awesome-icon icon="chart-line" />
                  <span>{{ $t('home.progressTracking') || 'Progress tracking' }}</span>
                </div>
                <div class="feature-item">
                  <font-awesome-icon icon="download" />
                  <span>{{ $t('home.exportPlans') || 'Export plans' }}</span>
                </div>
              </div>
              <router-link :to="{ name: 'trainingPlan' }" class="card-cta">
                <font-awesome-icon icon="play-circle" />
                {{ $t('home.startTraining') || 'Start Training' }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-content">
        <h1 class="hero-title">{{ $t('home.heroTitle') }}</h1>
        <p class="hero-subtitle">{{ $t('home.heroSubtitle') }}</p>
        <div class="hero-cta">
          <router-link :to="{ name: 'events' }" class="cta-btn primary">{{ $t('home.getStarted') }}</router-link>
          <a href="#features" class="cta-btn secondary">{{ $t('home.seeHowItWorks') }}</a>
        </div>
      </div>
      <div class="hero-visual">
        <img src="@/assets/runningwild-details.jpg" alt="Landing Visual" class="hero-img" />
      </div>
    </section>

    <!-- Features Section -->
    <section class="features-section" id="features">
      <h2 class="section-title">{{ $t('home.featuresTitle') }}</h2>
      <div class="features-list">
        <div class="feature-card">
          <font-awesome-icon icon="cogs" class="feature-icon" />
          <h3>{{ $t('home.trackYourGoal') }}</h3>
          <p>{{ $t('home.trackYourGoalDesc') }}</p>
        </div>
        <div class="feature-card">
          <font-awesome-icon icon="dumbbell" class="feature-icon" />
          <h3>{{ $t('home.featureMultiSport') }}</h3>
          <p>{{ $t('home.featureMultiSportDesc') }}</p>
        </div>
        <div class="feature-card">
          <font-awesome-icon icon="sliders-h" class="feature-icon" />
          <h3>{{ $t('home.featureRuleCustomization') }}</h3>
          <p>{{ $t('home.featureRuleCustomizationDesc') }}</p>
        </div>
        <div class="feature-card">
          <font-awesome-icon icon="calendar-alt" class="feature-icon" />
          <h3>{{ $t('home.featureTrainingPlans') }}</h3>
          <p>{{ $t('home.featureTrainingPlansDesc') }}</p>
        </div>
      </div>
    </section>

    <!-- Training Plans Section -->
    <section class="training-plans-section">
      <h2 class="section-title">{{ $t('home.trainingPlansTitle') }}</h2>
      <p class="section-subtitle">{{ $t('home.trainingPlansSubtitle') }}</p>
      <div class="training-plans-grid">
        <div class="training-plan-card">
          <div class="plan-card-header">
            <font-awesome-icon icon="medal" class="plan-icon" />
            <h3>{{ $t('home.halfMarathonPlan') }}</h3>
            <p>{{ $t('home.halfMarathonPlanDesc') }}</p>
          </div>
          <div class="plan-card-stats">
            <div class="plan-stat">
              <font-awesome-icon icon="calendar" class="stat-icon" />
              <span class="stat-value">16</span>
              <span class="stat-label">{{ $t('home.weeks') }}</span>
            </div>
            <div class="plan-stat">
              <font-awesome-icon icon="road" class="stat-icon" />
              <span class="stat-value">21.1km</span>
              <span class="stat-label">{{ $t('home.distance') }}</span>
            </div>
            <div class="plan-stat">
              <font-awesome-icon icon="clock" class="stat-icon" />
              <span class="stat-value">1:45-2:30</span>
              <span class="stat-label">{{ $t('home.targetTime') }}</span>
            </div>
          </div>
          <router-link :to="{ name: 'trainingPlan' }" class="plan-card-btn">
            <font-awesome-icon icon="play-circle" />
            {{ $t('home.startTrainingPlan') }}
          </router-link>
        </div>
        <div class="training-plan-card">
          <div class="plan-card-header">
            <font-awesome-icon icon="trophy" class="plan-icon" />
            <h3>{{ $t('home.fullMarathonPlan') }}</h3>
            <p>{{ $t('home.fullMarathonPlanDesc') }}</p>
          </div>
          <div class="plan-card-stats">
            <div class="plan-stat">
              <font-awesome-icon icon="calendar" class="stat-icon" />
              <span class="stat-value">16</span>
              <span class="stat-label">{{ $t('home.weeks') }}</span>
            </div>
            <div class="plan-stat">
              <font-awesome-icon icon="road" class="stat-icon" />
              <span class="stat-value">42.2km</span>
              <span class="stat-label">{{ $t('home.distance') }}</span>
            </div>
            <div class="plan-stat">
              <font-awesome-icon icon="clock" class="stat-icon" />
              <span class="stat-value">3:30-5:00</span>
              <span class="stat-label">{{ $t('home.targetTime') }}</span>
            </div>
          </div>
          <router-link :to="{ name: 'trainingPlan' }" class="plan-card-btn">
            <font-awesome-icon icon="play-circle" />
            {{ $t('home.startTrainingPlan') }}
          </router-link>
        </div>
      </div>
    </section>

    <!-- How It Works Section -->
    <section class="how-section">
      <h2 class="section-title">{{ $t('home.howItWorksTitle') }}</h2>
      <div class="how-steps">
        <div class="how-step">
          <font-awesome-icon icon="calendar-plus" class="how-icon" />
          <h4>{{ $t('home.howStep1') }}</h4>
        </div>
        <div class="how-step">
          <font-awesome-icon icon="sliders-h" class="how-icon" />
          <h4>{{ $t('home.howStep2') }}</h4>
        </div>
        <div class="how-step">
          <font-awesome-icon icon="users" class="how-icon" />
          <h4>{{ $t('home.howStep3') }}</h4>
        </div>
        <div class="how-step">
          <font-awesome-icon icon="chart-line" class="how-icon" />
          <h4>{{ $t('home.howStep4') }}</h4>
        </div>
                  </div>
    </section>

    <!-- Top Athletes Section -->
    <section class="top-section">
      <h2 class="section-title">{{ $t('home.topAthletes') }}</h2>
      <div class="top-list athletes">
        <div v-for="(ath, idx) in topAthletes" :key="idx" class="top-card athlete-card modern-athlete-card">
          <a :href="ath.strava_link" target="_blank" class="athlete-link" :aria-label="ath.name + ' Strava profile'">
            <div class="avatar-wrapper">
              <img :src="ath.profile" :alt="ath.name" class="avatar modern-avatar" />
            </div>
            <div class="athlete-info modern-athlete-info">
              <span class="athlete-name">{{ ath.name }}</span>
              <span class="athlete-activity" :title="$t('home.latestActivity') + ': ' + ath.latest_activity.name">
                <font-awesome-icon icon="bolt" class="activity-icon" />
                {{ ath.latest_activity.name }}
              </span>
              <span class="athlete-type">
                <font-awesome-icon :icon="getActivityIcon(ath.latest_activity.type)" class="type-icon" />
                {{ ath.latest_activity.type }}
              </span>
              <span class="athlete-distance">
                <font-awesome-icon icon="road" class="distance-icon" />
                {{ ath.latest_activity.km || 0 }} km
              </span>
            </div>
          </a>
        </div>
      </div>
    </section>

    <!-- Top Events Section -->
    <section class="top-section">
      <h2 class="section-title">{{ $t('home.topEvents') }}</h2>
      <div class="top-list events">
        <div v-for="(event, idx) in events" :key="idx" class="top-card event-card">
          <router-link :to="{ name: 'eventDetail', params: { slug: event.slug } }" class="event-link">
            <img :src="event.image" :alt="event.name" class="event-img" />
            <div class="event-info">
              <span class="event-name">{{ event.name }}</span>
              <span class="event-type">
                <font-awesome-icon v-if="event.type==='Swim'" icon="swimmer" />
                <font-awesome-icon v-if="event.type==='Ride'" icon="biking" />
                <font-awesome-icon v-if="event.type==='Run'" icon="running" />
                <font-awesome-icon v-if="event.type==='Walk'" icon="walking" />
                <span v-for="(type, typeIdx) in event.type" :key="typeIdx">
                  <font-awesome-icon v-if="type=='Swim'" icon="swimmer" />
                  <font-awesome-icon v-if="type=='Ride'" icon="biking" />
                  <font-awesome-icon v-if="type=='Run'" icon="running" />
                  <font-awesome-icon v-if="type=='Walk'" icon="walking" />
                </span>
              </span>
              <span class="event-dates">{{ formatEventDate(event.from * 1000) }} - {{ formatEventDate(event.to * 1000) }}</span>
              <span v-if="isMobile" class="event-fromts event-datetime">
                <font-awesome-icon icon="calendar-alt" class="event-datetime-icon" />
                {{ formatEventDateTime(event.from) }}
              </span>
              <span v-if="isMobile" class="event-tots event-datetime">
                <font-awesome-icon icon="flag-checkered" class="event-datetime-icon" />
                {{ formatEventDateTime(event.to) }}
              </span>
              <span class="event-active-status">
                <span v-if="isUpcomingEvent(event.from * 1000)" class="event-badge badge-upcoming">
                  <font-awesome-icon icon="clock" /> {{ $t('events.upcoming') }}
                </span>
                <span v-else-if="isExpiredEvent(event.to * 1000)" class="event-badge badge-expired">
                  <font-awesome-icon icon="times-circle" /> {{ $t('events.expired') }}
                </span>
                <span v-else class="event-badge badge-active">
                  <font-awesome-icon icon="check-circle" /> {{ $t('home.active') || 'Active' }}
                </span>
              </span>
            </div>
          </router-link>
        </div>
      </div>
    </section>

    <!-- Community/Stats Section -->
    <section class="stats-section">
      <div class="stats-list">
        <div class="stat-card">
          <font-awesome-icon icon="users" class="stat-icon" />
          <div class="stat-value">520k+</div>
          <div class="stat-label">{{ $t('home.participants') }}</div>
                  </div>
        <div class="stat-card">
          <font-awesome-icon icon="calendar-alt" class="stat-icon" />
          <div class="stat-value">700+</div>
          <div class="stat-label">{{ $t('home.eventsCreated') }}</div>
        </div>
        <div class="stat-card">
          <font-awesome-icon icon="dumbbell" class="stat-icon" />
          <div class="stat-value">20+</div>
          <div class="stat-label">{{ $t('home.sportsSupported') }}</div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <h2>{{ $t('home.ctaTitle') }}</h2>
      <p>{{ $t('home.ctaDesc') }}</p>
      <a href="https://forms.gle/ZR35ZekK2WgqR8Au7" target="_blank" rel="noopener" class="cta-btn primary">{{ $t('home.getStarted') }}</a>
    </section>

    </div>
  </div>
</template>

<script>
import $backend from '../backend'
import SubscriptionPricing from '@/components/SubscriptionPricing.vue'

export default {
  name: 'Home',
  components: {
    SubscriptionPricing
  },
  metaInfo () {
    return {
      title: this.$t('home.heroTitle')
    }
  },
  data () {
    return {
      // title is not needed in data, use $t directly in metaInfo
      athlete: {},
      topAthletes: [],
      events: [],
      error: '',
      code: '',
      isMobile: window.innerWidth <= 900
    }
  },
  created () {
    // Redirect to feed if user is logged in
    if (this.$store.state.isLoggedIn) {
      this.$router.push({ name: 'feed' })
      return
    }

    this.loadTopAthletes()
    this.loadEvents()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize () {
      this.isMobile = window.innerWidth <= 900
    },
    loadTopAthletes: function () {
      $backend.getTopAthletes(0, 5).then(
        response => {
          this.topAthletes = response.data.athletes
        }
      ).catch(
        error => {
          this.error = error.data.msg
        }
      )
      this.topAthletes = []
    },
    loadEvents: function () {
      $backend.getEvents(0, 5).then(response => {
        this.events = response.data.events
      }).catch(error => {
        this.error = error.data.msg
      })
    },
    getActivityIcon (type) {
      switch (type) {
        case 'Swim':
          return 'swimmer'
        case 'Ride':
          return 'biking'
        case 'Run':
          return 'running'
        case 'Walk':
          return 'walking'
        default:
          return 'info'
      }
    },
    formatEventDate (timestamp) {
      if (!timestamp) return 'N/A'
      const date = new Date(timestamp)
      return date.toLocaleDateString()
    },
    formatEventDateTime (timestamp) {
      if (!timestamp) return 'N/A'
      const date = new Date(timestamp * 1000)
      return date.toLocaleString()
    },
    isUpcomingEvent: function (fromTs) {
      return fromTs > Date.now()
    },
    isExpiredEvent: function (toTs) {
      return toTs < Date.now()
    },
    isEventActive (event) {
      const now = Math.floor(Date.now() / 1000)
      return event.from <= now && now <= event.to
    }
  }
}
</script>

<style scoped>
/* Sport light-blue theme */
/* Common styles */
.landing-container {
  font-family: 'Inter', Arial, sans-serif;
  background: linear-gradient(135deg, #fafcff 0%, #f0f9ff 30%, rgba(224, 242, 254, 0.4) 70%, #cffafe 100%);
  color: #222;
}

/* Redirect message for logged-in users */
.redirect-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
}

.redirect-message {
  text-align: center;
  padding: 3rem 2rem;
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 4px 20px rgba(74, 144, 226, 0.15);
  margin: 2rem;
}

.redirect-icon {
  font-size: 3rem;
  color: #4a90e2;
  margin-bottom: 1rem;
}

.redirect-message p {
  font-size: 1.1rem;
  color: #666;
  margin: 0;
}

/* Login CTA Section - Lucky & Energetic Theme */
.login-cta-section {
  padding: 3rem 1.5rem;
  background: linear-gradient(135deg, #0c4a6e 0%, #0ea5e9 50%, #0369a1 100%);
  color: white;
  border-radius: 2rem;
  margin: 2rem 0;
  position: relative;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(12, 74, 110, 0.3);
  border: 3px solid #38bdf8;
}

.login-cta-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%);
  pointer-events: none;
}

.login-cta-content {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 2rem;
  align-items: center;
  max-width: 1000px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.login-cta-visual {
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-icon-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-main-icon {
  font-size: 4rem;
  color: rgba(255, 255, 255, 0.9);
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
}

.icon-pulse {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120%;
  height: 120%;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.1);
    opacity: 0.7;
  }
}

.login-cta-text {
  text-align: center;
  flex: 1;
}

.login-cta-title {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  background: linear-gradient(45deg, #ffffff, #f8f9fa);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.login-cta-subtitle {
  font-size: 1.2rem;
  margin-bottom: 2rem;
  opacity: 0.95;
  line-height: 1.6;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.login-benefits {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 1rem;
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  opacity: 0.9;
}

.benefit-icon {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.8);
}

.login-cta-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.login-primary-btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 2.5rem;
  background: linear-gradient(135deg, #38bdf8, #7dd3fc);
  color: #0c4a6e;
  text-decoration: none;
  border-radius: 3rem;
  font-weight: 700;
  font-size: 1.1rem;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: hidden;
  box-shadow:
    0 8px 25px rgba(56, 189, 248, 0.5),
    0 4px 12px rgba(0, 0, 0, 0.1);
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.3);
  min-width: 220px;
  justify-content: center;
  border: 2px solid #fff;
}

.login-primary-btn:hover {
  transform: translateY(-4px) scale(1.05);
  box-shadow:
    0 15px 40px rgba(56, 189, 248, 0.6),
    0 6px 16px rgba(0, 0, 0, 0.15);
  color: #0c4a6e;
  text-decoration: none;
  background: linear-gradient(135deg, #7dd3fc, #38bdf8);
}

.login-primary-btn:active {
  transform: translateY(-1px) scale(1.02);
}

.btn-icon {
  font-size: 1.2rem;
}

.btn-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.6s ease;
}

.login-primary-btn:hover .btn-shine {
  left: 100%;
}

.login-help-text {
  font-size: 0.9rem;
  opacity: 0.8;
  margin: 0;
  text-align: center;
  font-style: italic;
}

.special-features-section {
  padding: 2rem 1.5rem 1.5rem 1.5rem;
  background: linear-gradient(135deg, #fafcff 0%, rgba(255, 249, 219, 0.8) 40%, rgba(224, 242, 254, 0.4) 100%);
  color: #333;
  border-radius: 1.5rem;
  margin: 1.5rem 0;
  text-align: center;
  position: relative;
  overflow: hidden;
  border: 2px solid #38bdf8;
}

.special-features-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="1" fill="rgba(74,144,226,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
  opacity: 0.5;
  pointer-events: none;
}

.special-features-title {
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #0c4a6e;
  position: relative;
  z-index: 1;
}

.special-features-subtitle {
  font-size: 1rem;
  color: #666;
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  z-index: 1;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  max-width: 1000px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.feature-card.special-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 1.5rem 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  color: #333;
}

.feature-card.special-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #0c4a6e, #38bdf8, #e0f2fe, #38bdf8, #0c4a6e);
  background-size: 200% 100%;
  animation: gradientShift 3s ease-in-out infinite;
}

@keyframes gradientShift {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.feature-card.special-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
  border-color: #43e97b;
}

.makekorlor-card {
  background: #fff;
}

.training-plan-card {
  background: #fff;
}

.card-visual {
  position: relative;
  margin-bottom: 1rem;
  display: flex;
  justify-content: center;
}

.card-icon {
  font-size: 3rem;
  color: #0c4a6e;
  margin-bottom: 0.5rem;
  transition: transform 0.3s ease;
}

.feature-card.special-card:hover .card-icon {
  transform: scale(1.05);
}

.card-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background: linear-gradient(135deg, #0c4a6e, #38bdf8);
  color: #fff;
  padding: 0.4em 0.8em;
  border-radius: 12px;
  font-size: 0.7em;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(12, 74, 110, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.03); }
}

.card-content {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.card-title {
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #333;
}

.card-description {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.card-features {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.8rem;
  margin-bottom: 1.5rem;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  color: #0c4a6e;
  font-weight: 600;
  padding: 0.4rem;
  background: linear-gradient(135deg, rgba(56, 189, 248, 0.2), rgba(224, 242, 254, 0.2));
  border-radius: 8px;
  transition: all 0.3s ease;
}

.feature-item:hover {
  background: linear-gradient(135deg, rgba(56, 189, 248, 0.35), rgba(224, 242, 254, 0.3));
  transform: translateX(4px) scale(1.02);
}

.feature-item svg {
  color: #38bdf8;
  font-size: 0.9rem;
}

.card-cta {
  background: linear-gradient(135deg, #4a90e2 0%, #43e97b 100%);
  color: #fff;
  padding: 0.8rem 1.5rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(74, 144, 226, 0.2);
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  position: relative;
  overflow: hidden;
}

.card-cta::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.card-cta:hover::before {
  left: 100%;
}

.card-cta:hover {
  background: linear-gradient(135deg, #43e97b 0%, #4a90e2 100%);
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(74, 144, 226, 0.3);
}

.card-cta svg {
  font-size: 1rem;
  transition: transform 0.3s ease;
}

.card-cta:hover svg {
  transform: translateX(2px);
}

.hero-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2rem 2rem 1.5rem 2rem;
  background: linear-gradient(135deg, #0c4a6e 0%, #0ea5e9 50%, #38bdf8 100%);
  color: #fff;
  border-radius: 0 0 2rem 2rem;
}
.hero-content {
  max-width: 600px;
  }
.hero-title {
  font-size: 2rem;
  font-weight: 800;
  margin-bottom: 0.75rem;
}
.hero-subtitle {
  font-size: 1rem;
  margin-bottom: 1.5rem;
}
.hero-cta {
  display: flex;
  gap: 1rem;
}
.cta-btn {
  padding: 0.75rem 2rem;
  border-radius: 2rem;
  font-weight: 700;
  font-size: 1rem;
  text-decoration: none;
  transition: background 0.2s, color 0.2s;
  box-shadow: 0 2px 8px rgba(74, 144, 226, 0.1);
  border: none;
  cursor: pointer;
}
.cta-btn.primary {
  background: #fff;
  color: #4a90e2;
    }
.cta-btn.primary:hover {
  background: #e3f2fd;
  }
.cta-btn.secondary {
  background: transparent;
  color: #fff;
  border: 2px solid #fff;
  }
.cta-btn.secondary:hover {
  background: #fff;
  color: #4a90e2;
  }
.hero-visual {
  flex: 1;
  display: flex;
  justify-content: flex-end;
}
.hero-img {
  max-width: 250px;
  border-radius: 2rem;
  box-shadow: 0 8px 32px rgba(0,0,0,0.08);
}
.features-section {
  padding: 3rem 2rem 2rem 2rem;
  background: #fff;
  border-radius: 2rem;
  margin: 2rem 0;
}
.section-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 2rem;
  color: #0c4a6e;
  text-align: center;
}
.features-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-top: 2rem;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
}
.feature-card {
  background: #fff;
  border-radius: 1rem;
  padding: 2rem 1.5rem;
  box-shadow: 0 2px 8px rgba(74, 144, 226, 0.08);
  text-align: center;
  position: relative;
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
}
.feature-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 24px rgba(74, 144, 226, 0.15);
}
.feature-icon {
  font-size: 2.5rem;
  color: #38bdf8;
  margin-bottom: 1rem;
}
.how-section {
  padding: 3rem 2rem 2rem 2rem;
  background: #f8f9fa;
  border-radius: 2rem;
  margin: 2rem 0;
  text-align: center;
}
.how-steps {
  display: flex;
  gap: 2rem;
  justify-content: center;
  flex-wrap: wrap;
  align-items: center;
}
.how-step {
  background: #fff;
  border-radius: 1rem;
  padding: 1.5rem 1rem;
  box-shadow: 0 2px 8px rgba(74, 144, 226, 0.08);
  text-align: center;
  max-width: 220px;
  flex: 1 1 160px;
  margin-left: auto;
  margin-right: auto;
}
.how-icon {
  font-size: 2rem;
  color: #c41e3a;
  margin-bottom: 0.5rem;
}
.top-section {
  padding: 2rem 2rem 1rem 2rem;
  background: #fff;
  border-radius: 2rem;
  margin: 2rem 0;
}
.top-list {
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
  max-width: 900px; /* Add this line */
  margin: 0 auto;   /* Add this line */
}
.top-card {
  background: #f8fbff;
  border-radius: 1rem;
  padding: 1.5rem 1rem;
  box-shadow: 0 2px 8px rgba(74, 144, 226, 0.08);
  text-align: center;
  max-width: 220px;
  flex: 1 1 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.athlete-link {
  text-decoration: none;
  color: #222;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 0 !important;
}
.avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #eee;
  margin-bottom: 0.25rem; /* Reduced margin */
}
.athlete-info {
  text-align: center;
  margin-bottom: 0; /* Remove extra margin */
}
.athlete-info > *:last-child {
  margin-bottom: 0 !important; /* Ensure no extra space below last child */
}
.athlete-name {
  font-weight: 700;
  font-size: 1.1rem;
  margin-bottom: 0.1rem; /* Reduced margin */
}
.athlete-activity {
  font-size: 0.9rem;
  color: #0c4a6e;
  margin-bottom: 0.1rem; /* Reduced margin */
}
.athlete-type {
  font-size: 0.85rem;
  color: #38bdf8;
  margin-bottom: 0.1rem; /* Reduced margin */
}
.athlete-distance {
  font-size: 0.9rem;
  color: #0369a1;
  margin-bottom: 0.1rem; /* Add or reduce margin if needed */
}
.event-link {
  text-decoration: none;
  color: #222;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.event-img {
  width: 100%;
  height: 140px; /* Increased from 80px */
  object-fit: cover; /* Changed from contain to cover for a more filled look */
  background: #fff;
  border-radius: 0.75rem;
  margin-bottom: 0.5rem;
}
.event-info {
  text-align: center;
}
.event-name {
  font-weight: 700;
  font-size: 1.1rem;
  margin-bottom: 0.2rem;
  max-width: 160px; /* limit width for ellipsis */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
}
.event-type {
  font-size: 0.9rem;
  color: #43e97b;
  margin-bottom: 0.2rem;
}
.event-dates {
  font-size: 0.85rem;
  color: #4a90e2;
}
.stats-section {
  padding: 2rem 2rem 1rem 2rem;
  background: #f8f9fa;
  border-radius: 2rem;
  margin: 2rem 0;
}
.stats-list {
  display: flex;
  gap: 2rem;
  justify-content: center;
  flex-wrap: wrap;
  max-width: 900px; /* Add this line */
  margin: 0 auto;   /* Add this line */
  align-items: center;
}
.stat-card {
  background: #fff;
  border-radius: 1rem;
  padding: 1.5rem 1rem;
  box-shadow: 0 2px 8px rgba(74, 144, 226, 0.08);
  text-align: center;
  max-width: 220px;
  flex: 1 1 160px;
  }
.stat-icon {
  font-size: 2rem;
  color: #c41e3a;
  margin-bottom: 0.5rem;
}
.stat-value {
  font-size: 1.3rem;
  font-weight: 800;
  color: #d4af37;
}
.stat-label {
  font-size: 1rem;
  color: #c41e3a;
}
.cta-section {
  padding: 3rem 2rem 2rem 2rem;
  background: linear-gradient(135deg, #c41e3a 0%, #d4af37 100%);
  color: #fff;
  border-radius: 2rem;
  text-align: center;
  margin: 2rem 0;
}
.footer {
  background: #fff;
  padding: 1.5rem 2rem;
  border-radius: 2rem 2rem 0 0;
  text-align: center;
  color: #888;
  margin-top: 2rem;
}
.footer-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}
.footer-links {
  display: flex;
  gap: 1.5rem;
}
.event-card {
  border-radius: 0.75rem;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  transition: box-shadow 0.15s, transform 0.15s;
  background: #fff;
  min-width: 0;
}
.event-card .card-body {
  padding: 0.75rem 0.5rem 0.5rem 0.5rem;
}
.event-link:hover .event-card {
  box-shadow: 0 6px 24px rgba(0,0,0,0.18);
  transform: translateY(-2px) scale(1.025);
}
.top-list.athletes {
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
  max-width: 900px; /* Add this line */
  margin: 0 auto;   /* Add this line */
}
.top-card.athlete-card {
  border-radius: 1rem;
  padding: 0.75rem 1rem 0.15rem 1rem;
  box-shadow: 0 2px 8px rgba(74, 144, 226, 0.08);
  text-align: center;
  max-width: 320px;
  margin-left: auto;
  align-items: center;
  margin-right: auto;
  margin-bottom: 0.1rem;
}
.top-list.events {
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
  align-items: center; /* Add this line */
}
.top-card.event-card {
  margin-left: auto; /* Add this line */
  margin-right: auto; /* Add this line */
}
/* Modern Athlete Card Styles - Lucky & Energetic Theme */
.modern-athlete-card {
  position: relative;
  background: linear-gradient(135deg, #fafcff 60%, rgba(224, 242, 254, 0.3) 100%);
  border: 2px solid #38bdf8;
  box-shadow: 0 4px 20px rgba(56, 189, 248, 0.2);
  transition: all 0.3s ease;
  padding: 1.25rem 1.25rem 0.5rem 1.25rem;
  max-width: 340px;
  text-align: center;
  align-items: center;
}
.avatar-wrapper, .modern-athlete-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.modern-athlete-card:hover {
  box-shadow: 0 12px 35px rgba(12, 74, 110, 0.2);
  transform: translateY(-6px) scale(1.03);
  border: 2px solid #0c4a6e;
}
.avatar-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 0.5rem;
}
.modern-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #38bdf8;
  box-shadow: 0 4px 15px rgba(56, 189, 248, 0.3);
  background: #fff;
  transition: all 0.3s ease;
}
.modern-athlete-card:hover .modern-avatar {
  border: 3px solid #0c4a6e;
  box-shadow: 0 6px 20px rgba(12, 74, 110, 0.3);
}
.modern-athlete-info {
  text-align: center;
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}
.athlete-name {
  font-weight: 800;
  font-size: 1.15rem;
  margin-bottom: 0.1rem;
  color: #222;
}
.athlete-activity {
  font-size: 0.95rem;
  color: #4a90e2;
  margin-bottom: 0.1rem;
  display: flex;
  align-items: center;
  gap: 0.3em;
}
.athlete-type {
  font-size: 0.9rem;
  color: #43e97b;
  margin-bottom: 0.1rem;
  display: flex;
  align-items: center;
  gap: 0.3em;
}
.athlete-distance {
  font-size: 0.95rem;
  color: #ff6b35;
  margin-bottom: 0.1rem;
  display: flex;
  align-items: center;
  gap: 0.3em;
}
.activity-icon, .type-icon, .distance-icon {
  font-size: 1em;
  opacity: 0.85;
}
.event-datetime {
  display: flex;
  align-items: center;
  gap: 0.3em;
  font-size: 0.95rem;
  color: #4a90e2;
  margin-bottom: 0.1rem;
}
.event-datetime-icon {
  font-size: 1em;
  opacity: 0.85;
}
.event-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.3em;
  font-size: 0.8em;
  padding: 0.3em 0.8em;
  border-radius: 0.7em;
  font-weight: 600;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  margin-left: 0.5em;
}
.badge-active {
  background: #43e97b;
  color: #fff;
}
.badge-upcoming {
  background: #17a2b8;
  color: #fff;
}
.badge-expired {
  background: #dc3545;
  color: #fff;
}
.training-plans-section {
  padding: 3rem 2rem 2rem 2rem;
  background: #f8f9fa;
  border-radius: 2rem;
  margin: 2rem 0;
  text-align: center;
}
.training-plans-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
}
.training-plan-card {
  background: #fff;
  border-radius: 1rem;
  padding: 2rem 1.5rem;
  box-shadow: 0 2px 8px rgba(74, 144, 226, 0.08);
  text-align: center;
  position: relative;
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
}
.training-plan-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 24px rgba(74, 144, 226, 0.15);
}
.plan-card-header {
  margin-bottom: 1.5rem;
}
.plan-icon {
  font-size: 3rem;
  color: #0c4a6e;
  margin-bottom: 0.5rem;
}
.plan-card-header h3 {
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #222;
}
.plan-card-header p {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 1.5rem;
}
.plan-card-stats {
  display: flex;
  justify-content: space-around;
  margin-bottom: 1.5rem;
  padding: 0.5rem 0;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;
}
.plan-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.stat-icon {
  font-size: 1.5rem;
  color: #38bdf8;
  margin-bottom: 0.3rem;
}
.stat-value {
  font-size: 1.2rem;
  font-weight: 800;
  color: #38bdf8;
}
.stat-label {
  font-size: 0.8rem;
  color: #0c4a6e;
}
.plan-card-btn {
  background: linear-gradient(135deg, #0c4a6e 0%, #38bdf8 100%);
  color: #fff;
  padding: 0.75rem 2rem;
  border-radius: 2rem;
  font-weight: 700;
  font-size: 1rem;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(12, 74, 110, 0.3);
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}
  .plan-card-btn:hover {
    background: linear-gradient(135deg, #38bdf8 0%, #0c4a6e 100%);
    color: #fff;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(12, 74, 110, 0.4);
  }
  .plan-card-btn .fa-play-circle {
    font-size: 1rem;
  }
  .section-subtitle {
    font-size: 1.1rem;
    color: #666;
    margin-bottom: 2rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }

@media (max-width: 900px) {
  .login-cta-section {
    padding: 2rem 1rem;
    margin: 1rem 0;
    border-radius: 1.5rem;
  }

  .login-cta-content {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    text-align: center;
  }

  .login-cta-title {
    font-size: 2rem;
  }

  .login-cta-subtitle {
    font-size: 1.1rem;
    margin-bottom: 1.5rem;
  }

  .login-benefits {
    flex-direction: column;
    gap: 1rem;
    align-items: center;
  }

  .login-primary-btn {
    padding: 1rem 2rem;
    font-size: 1rem;
    min-width: 200px;
  }

  .special-features-section {
    padding: 1.5rem 1rem 1rem 1rem;
    margin: 1rem 0;
    max-width: 100vw;
  }

  .special-features-title {
    font-size: 1.6rem;
    margin-bottom: 0.5rem;
  }

  .special-features-subtitle {
    font-size: 0.9rem;
    margin-bottom: 1.5rem;
  }

  .features-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
    max-width: 100%;
  }

  .feature-card.special-card {
    padding: 1.2rem 0.8rem;
    margin: 0 auto;
    max-width: 100%;
  }

  .card-icon {
    font-size: 2.5rem;
  }

  .card-title {
    font-size: 1.2rem;
  }

  .card-description {
    font-size: 0.85rem;
    margin-bottom: 1.2rem;
  }

  .card-features {
    grid-template-columns: 1fr;
    gap: 0.6rem;
    margin-bottom: 1.2rem;
  }

  .feature-item {
    font-size: 0.8rem;
    padding: 0.3rem;
  }

  .card-cta {
    padding: 0.7rem 1.2rem;
    font-size: 0.85rem;
  }

  .hero-section {
    flex-direction: column;
    text-align: center;
    padding: 2rem 1rem 1rem 1rem;
}
  .hero-visual {
    justify-content: center;
    margin-top: 1.5rem;
}
  .features-list, .how-steps, .top-list, .stats-list, .training-plans-grid {
    flex-direction: column;
    gap: 1.5rem;
  }
  .features-list {
    grid-template-columns: 1fr;
  }
  .modern-athlete-card {
    max-width: 95vw;
    width: 100%;
    padding: 1rem 0.5rem 0.5rem 0.5rem;
  }
  .modern-avatar {
    width: 48px;
    height: 48px;
  }
  .training-plan-card {
    padding: 1.5rem 1rem;
  }
  .plan-card-header {
    margin-bottom: 1rem;
  }
  .plan-icon {
    font-size: 2.5rem;
  }
  .plan-card-header h3 {
    font-size: 1.1rem;
  }
  .plan-card-header p {
    font-size: 0.8rem;
  }
  .plan-card-stats {
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.3rem 0;
    border-top: none;
    border-bottom: none;
  }
  .stat-icon {
    font-size: 1.2rem;
  }
  .stat-value {
    font-size: 1rem;
  }
  .stat-label {
    font-size: 0.7rem;
  }
  .plan-card-btn {
    padding: 0.6rem 1.5rem;
    font-size: 0.9rem;
  }
  .plan-card-btn .fa-play-circle {
    font-size: 0.9rem;
  }
}
@media (min-width: 901px) {
  .event-dates {
    display: block;
    margin-left: auto;
    margin-right: auto;
    text-align: center;
  }
}
@media (max-width: 600px) {
  .login-cta-section {
    padding: 1.5rem 0.75rem;
    margin: 0.5rem 0;
    border-radius: 1rem;
  }

  .login-cta-title {
    font-size: 1.8rem;
    margin-bottom: 0.75rem;
  }

  .login-cta-subtitle {
    font-size: 1rem;
    margin-bottom: 1.25rem;
  }

  .login-main-icon {
    font-size: 3rem;
  }

  .login-primary-btn {
    padding: 0.875rem 1.5rem;
    font-size: 0.95rem;
    min-width: 180px;
  }

  .login-help-text {
    font-size: 0.85rem;
  }

  .special-features-section {
    padding: 1rem 0.6rem 0.8rem 0.6rem;
    margin: 0.5rem 0;
    max-width: 100vw;
  }

  .special-features-title {
    font-size: 1.5rem;
    margin-bottom: 0.4rem;
  }

  .special-features-subtitle {
    font-size: 0.85rem;
    margin-bottom: 1.2rem;
  }

  .features-grid {
    gap: 0.8rem;
    max-width: 100vw;
  }

  .feature-card.special-card {
    padding: 1rem 0.6rem;
    max-width: 100vw;
  }

  .card-icon {
    font-size: 2rem;
  }

  .card-title {
    font-size: 1.1rem;
  }

  .card-description {
    font-size: 0.8rem;
    margin-bottom: 1rem;
  }

  .card-features {
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  .feature-item {
    font-size: 0.75rem;
    padding: 0.25rem;
  }

  .card-cta {
    padding: 0.6rem 1rem;
    font-size: 0.8rem;
  }

  .card-badge {
    font-size: 0.6em;
    padding: 0.3em 0.6em;
  }

  .top-list.athletes {
    gap: 0.75rem;
    justify-content: center;
  }
  .top-card.athlete-card {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    max-width: 95vw;
    width: 100%;
    /* Center the card */
    margin-left: auto;
    margin-right: auto;
  }
  .avatar {
    width: 36px;
    height: 36px;
    margin-bottom: 0.15rem;
  }
  .athlete-info {
    font-size: 0.85rem;
  }
  .athlete-name {
    font-size: 0.95rem;
    margin-bottom: 0.05rem;
  }
  .athlete-activity, .athlete-type, .athlete-distance {
    font-size: 0.75rem;
    margin-bottom: 0.05rem;
  }
}
</style>
